// UI shell state (IMPLEMENTATION §2.1 ui/store.ts, §2.7 overlay states +SHEET / +SELECTED): Preact signals the
// components read. The input router writes mode / selection / hover zone / dragging through main.ts's
// InputUi; the shell controller (ui/shell.ts) writes the panels, toasts, Sarge's line, the intro phase and the
// banners. One panel at a time (Shop sheet, Settings, goal card or a card); the Shop sheet additionally
// collapses to its 28 px peek while a drag runs (M6 decision 12, §2.8 step 3).
import { signal } from '@preact/signals';
import type { ZoneId } from '../input/dropZones.ts';
import type { ModeView, SelectionView } from '../input/router.ts';
import type { Layout } from '../layout/computeLayout.ts';
import type { BType } from '../sim/gen/ids.ts';
import type { CardTarget } from './cards/model.ts';
import { type IntroState, startIntro } from './intro.ts';
import type { ShopCardModel } from './shopModel.ts';
import { NO_TOASTS, type ToastState } from './toastQueue.ts';

export type SheetState = 'closed' | 'open' | 'peek';

export type Panel =
  | { k: 'none' }
  | { k: 'shop' }
  | { k: 'settings' }
  | { k: 'goal' }
  | { k: 'card'; target: CardTarget; more: boolean };

export const NO_PANEL: Panel = { k: 'none' };

export const layoutSig = signal<Layout | null>(null);
export const modeSig = signal<ModeView | null>(null);
export const selectionSig = signal<SelectionView | null>(null);
export const hoverZoneSig = signal<ZoneId | null>(null);
/** A drag runs: toasts go 30% and pointer-transparent, tabs are inert, the Shop sheet peeks. */
export const draggingSig = signal(false);
export const panelSig = signal<Panel>(NO_PANEL);
/** The Shop sheet: open / collapsed to its peek during a drag / closed (M0 `__gm.drag().sheet`). */
export const sheetSig = signal<SheetState>('closed');
/** The Shop card whose detail pop-over (Place) is open. */
export const popoverSig = signal<ShopCardModel | null>(null);
export const toastsSig = signal<ToastState>(NO_TOASTS);
/** Sarge's speech bubble (a scripted line, a tip or a transient hint), null = quiet. */
export const sargeSig = signal<{ text: string; key: string } | null>(null);
export const introSig = signal<IntroState>(startIntro(false, 0));
/** One-time rule banner / the v0.1 completion banner. */
export const bannerSig = signal<'rule' | 'complete' | null>(null);
/** The building how-to card on show (a building type), null = none. */
export const howtoSig = signal<BType | null>(null);
/** Shop items revealed since the Shop was last looked at (NEW! badges, the Shop tab dot). */
export const freshSig = signal<readonly string[]>([]);
/** The phone is held in landscape: the RotateOverlay's text (null = portrait); it blocks input, the sim runs. */
export { rotateSig } from './overlays/store.ts';
/** The ⟳ handle after a placement (R14: 3 s, in both modes). */
export const rotateHandleSig = signal<{ bid: number; until: number } | null>(null);
/** Transient status (tap-placement: "Tap a cell to place Lead Vein"). */
export const statusSig = signal<string | null>(null);
/** How many of the current goal's earlier chips have shown (ui.json goalChip.introMs each). */
export const chipIntroSig = signal(0);
/** Settings → Export telemetry fell back to the copy textarea: its text (null = hidden). */
export const copyTextSig = signal<string | null>(null);

/** Preact renders of the HUD (the M7 "HUD renders ≤ 25 times in 2 s" bar, `__gm.ui().hudRenders`). */
export const renderCounts = { hud: 0 };
